/**
 * Project/preset file formats — the ONLY place that defines serialised shape
 * (AGENT.md §13, docs/project-format.md). Everything imported is untrusted.
 */
import { z } from "zod";
import { DRUM_LANES } from "../sequencer/patterns";
import { MAX_BPM, MIN_BPM } from "../sequencer/time";
import { keyTonics } from "../theory/key";

export const PROJECT_FORMAT = "music-platform/project";
export const PRESET_FORMAT = "music-platform/preset";
export const CURRENT_PROJECT_VERSION = 4;
export const SETLIST_FORMAT = "music-platform/setlist";
export const CURRENT_SETLIST_VERSION = 1;
export const CURRENT_PRESET_VERSION = 1;

const unit = z.number().min(0).max(1);

/** External links are untrusted: https only, parsed with URL (AGENT.md §15). */
export const httpsUrl = z.string().max(2000).refine((v) => {
  try { return new URL(v).protocol === "https:"; } catch { return false; }
}, { message: "Only https:// links are allowed" });

export const referenceSchema = z.object({ label: z.string().min(1).max(120), url: httpsUrl });
const waveform = z.enum(["sine", "triangle", "sawtooth", "square"]);

export const synthParamsSchema = z.object({
  waveform, osc2Waveform: waveform, osc2Mix: unit, osc2DetuneCents: z.number().min(-1200).max(1200),
  attackSec: z.number().min(0).max(10), decaySec: z.number().min(0).max(10), sustain: unit, releaseSec: z.number().min(0).max(20),
  cutoffHz: z.number().min(20).max(20000), resonance: z.number().min(0.0001).max(30), volume: unit,
  maxVoices: z.number().int().min(1).max(32),
});

export const drumKitParamsSchema = z.object({ volume: unit, tune: z.number().min(-12).max(12), decay: z.number().min(0.25).max(2) });

export const samplerParamsSchema = z.object({
  rootNote: z.number().int().min(0).max(127), volume: unit, mode: z.enum(["oneshot", "gate"]),
  attackSec: z.number().min(0).max(5), releaseSec: z.number().min(0).max(10),
});

export const multiSampleParamsSchema = z.object({
  volume: unit, attackSec: z.number().min(0).max(5), releaseSec: z.number().min(0).max(10),
});

/** Id of a built-in sampled instrument (public/sounds/index.json). */
export const soundIdSchema = z.string().regex(/^[a-z0-9-]{1,40}$/);

export const instrumentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("synth"), params: synthParamsSchema }),
  z.object({ kind: z.literal("drumkit"), params: drumKitParamsSchema }),
  z.object({ kind: z.literal("sampler"), params: samplerParamsSchema, assetId: z.uuid().nullable() }),
  // v4: built-in sampled instrument (real recordings).
  z.object({ kind: z.literal("multisample"), params: multiSampleParamsSchema, soundId: soundIdSchema }),
]);

export const stripSchema = z.object({
  volume: z.number().min(0).max(1.5), pan: z.number().min(-1).max(1), mute: z.boolean(), solo: z.boolean(),
  sendReverb: unit, sendDelay: unit,
});

export const trackSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(60),
  instrument: instrumentSchema,
  mixer: stripSchema,
});

const keySchema = z.object({ tonic: z.string(), mode: z.enum(["major", "minor"]) })
  .refine((k) => keyTonics(k.mode).includes(k.tonic), { message: "Unsupported key tonic" });

const lanesShape = Object.fromEntries(DRUM_LANES.map((l) => [l, z.array(z.boolean()).max(64)])) as Record<(typeof DRUM_LANES)[number], z.ZodArray<z.ZodBoolean>>;

export const projectObjectSchema = z.object({
  format: z.literal(PROJECT_FORMAT),
  schemaVersion: z.literal(CURRENT_PROJECT_VERSION),
  id: z.uuid(),
  name: z.string().min(1).max(120),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  revision: z.number().int().min(0),
  song: z.object({
    title: z.string().max(200),
    artist: z.string().max(200),
    key: keySchema,
    transposeSemitones: z.number().int().min(-12).max(12),
    transposeLiveInput: z.boolean(),
    tempoBpm: z.number().min(MIN_BPM).max(MAX_BPM),
    timeSignature: z.object({ beatsPerBar: z.number().int().min(2).max(7), beatUnit: z.literal(4) }),
    /** Performance notes for the band (v3). */
    notes: z.string().max(4000),
    /** Links to charts, recordings, etc. the user is entitled to use (v3). */
    references: z.array(referenceSchema).max(10),
  }),
  tracks: z.array(trackSchema).min(1).max(32),
  sequence: z.object({
    drums: z.object({ trackId: z.uuid(), pattern: z.object({ steps: z.number().int().min(1).max(64), lanes: z.object(lanesShape) }) }),
    progression: z.object({
      trackId: z.uuid(),
      chords: z.array(z.object({ degree: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6), z.literal(7)]), seventh: z.boolean() })).max(16),
      octave: z.number().int().min(1).max(6),
      inversion: z.number().int().min(0).max(3),
      rhythm: z.enum(["whole", "half", "quarters", "eighths"]),
      bassNote: z.boolean(),
      enabled: z.boolean(),
    }),
  }),
  effects: z.object({
    reverb: z.object({ decaySec: z.number().min(0.1).max(10), returnLevel: unit }),
    delay: z.object({ timeSec: z.number().min(0.01).max(2), feedback: z.number().min(0).max(0.9), toneHz: z.number().min(200).max(18000), returnLevel: unit }),
  }),
  master: z.object({ volume: unit }),
  metronome: z.object({ enabled: z.boolean(), volume: unit, countInBars: z.number().int().min(0).max(4) }),
  playground: z.object({ keyboardTrackId: z.uuid(), padsTrackId: z.uuid(), octave: z.number().int().min(0).max(7) }),
  /** Unknown fields from newer/other tools are preserved here, never dropped silently. */
  extensions: z.record(z.string(), z.unknown()),
});

export const projectSchema = projectObjectSchema.superRefine((p, ctx) => {
  const ids = new Set(p.tracks.map((t) => t.id));
  if (ids.size !== p.tracks.length) ctx.addIssue({ code: "custom", message: "Duplicate track ids", path: ["tracks"] });
  for (const [path, id] of [["sequence.drums.trackId", p.sequence.drums.trackId], ["sequence.progression.trackId", p.sequence.progression.trackId], ["playground.keyboardTrackId", p.playground.keyboardTrackId], ["playground.padsTrackId", p.playground.padsTrackId]] as const) {
    if (!ids.has(id)) ctx.addIssue({ code: "custom", message: `${path} references a missing track`, path: path.split(".") });
  }
});

export type Project = z.infer<typeof projectSchema>;
export type Track = z.infer<typeof trackSchema>;
export type InstrumentConfig = z.infer<typeof instrumentSchema>;
export type InstrumentKind = InstrumentConfig["kind"];
export type SynthConfigParams = Extract<InstrumentConfig, { kind: "synth" }>["params"];
export type StripConfig = Track["mixer"];

export const presetSchema = z.object({
  format: z.literal(PRESET_FORMAT),
  schemaVersion: z.literal(CURRENT_PRESET_VERSION),
  id: z.uuid(),
  name: z.string().min(1).max(120),
  tags: z.array(z.string().max(40)).max(20),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  instrument: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("synth"), params: synthParamsSchema }),
    z.object({ kind: z.literal("drumkit"), params: drumKitParamsSchema }),
    z.object({ kind: z.literal("sampler"), params: samplerParamsSchema }),
  ]),
});

export type Preset = z.infer<typeof presetSchema>;

export const licenceSchema = z.object({
  kind: z.enum(["user-provided", "cc0", "cc-by", "cc-by-sa", "cc-by-nc", "proprietary", "unknown"]),
  attribution: z.string().max(500).optional(),
  sourceUrl: z.string().max(2000).optional(),
});

export type Licence = z.infer<typeof licenceSchema>;

/** Asset metadata stored alongside the blob. */
export interface AssetRecord {
  id: string;
  name: string;
  type: "sample";
  mimeType: string;
  sizeBytes: number;
  durationSec: number;
  sampleRate: number;
  channels: number;
  createdAt: string;
  source: "user-import";
  licence: Licence;
  blob: Blob;
}

export const setlistEntrySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  /** Overrides applied (as an undoable edit) when the song is opened from the setlist; null = use the arrangement's own. */
  transposeSemitones: z.number().int().min(-12).max(12).nullable(),
  tempoBpm: z.number().min(MIN_BPM).max(MAX_BPM).nullable(),
  notes: z.string().max(1000),
});

export const setlistSchema = z.object({
  format: z.literal(SETLIST_FORMAT),
  schemaVersion: z.literal(CURRENT_SETLIST_VERSION),
  id: z.uuid(),
  name: z.string().min(1).max(120),
  /** Optional event date (YYYY-MM-DD). */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  entries: z.array(setlistEntrySchema).max(100),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  revision: z.number().int().min(0),
});

export type Setlist = z.infer<typeof setlistSchema>;
export type SetlistEntry = z.infer<typeof setlistEntrySchema>;
